import {
  IRoleType,
  ISubmissionWorkflowState
} from '@publishing-engine/domain/models';
import { IGetSubmissionWorkflowResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { EditorialRevisionAdditionalInfo } from './editorial/Revision';
import InitialAssessmentAdditionalInfo from './editorial/InitialAssessment';
import { PeerReviewAdditionalInfo } from './editorial/PeerReview';
import { AuthorRevisionAdditionalInfo } from './author/Revision';
import { EditorialRevisionAssessmentAdditionalInfo } from './editorial/RevisionAssessment';
import { ReviewerRevisionAssessmentInfo } from './reviewer/RevisionAssessment';
import { ReviewerPeerReviewAdditionalInfo } from './reviewer/PeerReview';
import { ReviewerRevisionAdditionalInfo } from './reviewer/ReviewerRevisionAdditionalInfo';
import { EditorialTransferOfferedAdditionalInfo } from './editorial/TransferOffered';
import { AuthorTransferOfferedAdditionalInfo } from './author/TransferOffered';
import { ReviewerTransferOfferedAdditionalInfo } from './reviewer/ReviewerTransferOfferedAdditionalInfo';
import { EditorialRejectedAdditionalInfo } from './editorial/Rejected';
import { ReviewerRejectedAdditionalInfo } from './reviewer/ReviewerRejectedAdditionalInfo';
import { EditorialPreProductionAdditionalInformation } from './editorial/PreProduction';
import { ReviewerPreProductionAdditionalInfo } from './reviewer/ReviewerPreProductionAdditionalInfo';
import { AuthorPreProductionAdditionalInfo } from './author/ChangeRequest';
import { EditorialWithdrawnAdditionalInfo } from './editorial/Withdrawn';
import { AuthorWithdrawnAdditionalInfo } from './author/Withdrawn';
import { EditorialUnsubmittedAdditionalInfo } from './editorial/Unsubmitted';
import { AuthorUnsubmittedAdditionalInfo } from './author/Unsubmitted';
import { ExpertPeerReviewAdditionalInfo } from './expert/PeerReview';

const phaseRoleAdditionalInfo: Partial<
  Record<
    ISubmissionWorkflowState,
    Record<IRoleType, ((props: any) => JSX.Element) | null>
  >
> = {
  initialAssessment: {
    author: null,
    editorial: InitialAssessmentAdditionalInfo,
    reviewer: null,
    expert: ExpertPeerReviewAdditionalInfo
  },
  peerReviewInvitation: {
    author: null,
    editorial: PeerReviewAdditionalInfo,
    reviewer: ReviewerPeerReviewAdditionalInfo,
    expert: ExpertPeerReviewAdditionalInfo
  },
  peerReviewPending: {
    author: null,
    editorial: PeerReviewAdditionalInfo,
    reviewer: ReviewerPeerReviewAdditionalInfo,
    expert: ExpertPeerReviewAdditionalInfo
  },
  peerReviewFinished: {
    author: null,
    editorial: PeerReviewAdditionalInfo,
    reviewer: ReviewerPeerReviewAdditionalInfo,
    expert: ExpertPeerReviewAdditionalInfo
  },
  revision: {
    author: AuthorRevisionAdditionalInfo,
    editorial: EditorialRevisionAdditionalInfo,
    reviewer: ReviewerRevisionAdditionalInfo,
    expert: ExpertPeerReviewAdditionalInfo
  },
  revisionAssessment: {
    author: null,
    editorial: EditorialRevisionAssessmentAdditionalInfo,
    reviewer: ReviewerRevisionAssessmentInfo,
    expert: ExpertPeerReviewAdditionalInfo
  },
  revisionPeerReviewInvitation: {
    author: null,
    editorial: EditorialRevisionAssessmentAdditionalInfo,
    reviewer: ReviewerRevisionAssessmentInfo,
    expert: ExpertPeerReviewAdditionalInfo
  },
  revisionPeerReviewPending: {
    author: null,
    editorial: EditorialRevisionAssessmentAdditionalInfo,
    reviewer: ReviewerRevisionAssessmentInfo,
    expert: ExpertPeerReviewAdditionalInfo
  },
  revisionPeerReviewFinished: {
    author: null,
    editorial: EditorialRevisionAssessmentAdditionalInfo,
    reviewer: ReviewerRevisionAssessmentInfo,
    expert: ExpertPeerReviewAdditionalInfo
  },
  transferOffered: {
    author: AuthorTransferOfferedAdditionalInfo,
    editorial: EditorialTransferOfferedAdditionalInfo,
    reviewer: ReviewerTransferOfferedAdditionalInfo,
    expert: ExpertPeerReviewAdditionalInfo
  },
  rejected: {
    author: null,
    editorial: EditorialRejectedAdditionalInfo,
    reviewer: ReviewerRejectedAdditionalInfo,
    expert: null
  },
  preProduction: {
    author: AuthorPreProductionAdditionalInfo,
    editorial: EditorialPreProductionAdditionalInformation,
    reviewer: ReviewerPreProductionAdditionalInfo,
    expert: ExpertPeerReviewAdditionalInfo
  },
  withdrawn: {
    author: AuthorWithdrawnAdditionalInfo,
    editorial: EditorialWithdrawnAdditionalInfo,
    reviewer: null,
    expert: null
  },
  unsubmitted: {
    author: AuthorUnsubmittedAdditionalInfo,
    editorial: EditorialUnsubmittedAdditionalInfo,
    reviewer: null,
    expert: ExpertPeerReviewAdditionalInfo
  },
  immediateRevision: {
    author: AuthorRevisionAdditionalInfo,
    editorial: EditorialRevisionAdditionalInfo,
    reviewer: null,
    expert: ExpertPeerReviewAdditionalInfo
  }
};

const getAdditionalInfoForPhase = (
  phase: ISubmissionWorkflowState,
  role: IRoleType
) => phaseRoleAdditionalInfo[phase]?.[role];

export const AdditionalInformation = ({
  role,
  workflowResponse,
  setActiveTab,
  entityType
}: {
  role: IRoleType;
  workflowResponse: IGetSubmissionWorkflowResponse;
  setActiveTab?: (tab: any) => void;
  entityType?: string;
}) => {
  const Component = getAdditionalInfoForPhase(
    workflowResponse.workflowData.currentState as ISubmissionWorkflowState,
    role
  );

  return Component ? (
    <Component setActiveTab={setActiveTab} entityType={entityType} />
  ) : null;
};
